import React, { ElementType, ReactNode } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { ColorPath } from 'types/Theme';
import { getCssColorByPath } from '../../../utils/colors';
import { getLinariaClassName } from '../../../utils/getLinariaClassName';
export type TitleVariant = 'titleXS' | 'titleS' | 'titleM' | 'titleL' | 'titleXL' | 'title2XL' | 'title3XL' | 'title4XL' | 'title5XL';
export type BodyVariant = 'bodyXS' | 'bodyS' | 'bodyM' | 'bodyL';
export type LabelVariant = 'labelXS' | 'labelS' | 'labelM';
export type TextVariant = BodyVariant | LabelVariant | TitleVariant;
export type TextAlign = 'inherit' | 'center' | 'end' | 'start' | 'justify' | 'left' | 'right';
interface TextProps {
  /** HTML Element that text will be rendered */
  /** @deprecated Linaria doesn't support `as` prop usage for the styled component. Use `tag` prop instead*/
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  as?: ElementType<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tag?: ElementType<any>;
  /** Specify the color path from the theme's colors file to explicitly set the Text's color */
  colorPath?: ColorPath;
  /** The text variant style from the theme */
  variant?: TextVariant;
  /** HTML class to be included in the tag */
  className?: string;
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string;
  /** Text to be displayed */
  children: ReactNode;
  /** text-align style */
  align?: TextAlign;
  id?: string;
  style?: React.CSSProperties;
  /** Allow disabling of in-browser translation */
  translate?: 'no';
}
interface StyleProps {
  align: TextAlign;
  colorValue?: string;
}

/** We don't want to change Text component interface for developers, so we should still accept all possible values, but convert them into the proper ones */
const _exp17 = /*#__PURE__*/() => ({
  align
}) => ({
  inherit: "inherit",
  center: "center",
  end: "end",
  start: "start",
  justify: "justify",
  left: "start",
  right: "end"
})[align];
const _exp18 = /*#__PURE__*/() => ({
  colorValue
}) => colorValue || 'initial';
const TextElement = /*#__PURE__*/styled('p')({
  name: "TextElement",
  class: "t9z2x5h",
  propsAsIs: false,
  vars: {
    "t9z2x5h-0": [_exp17()],
    "t9z2x5h-1": [_exp18()]
  }
});
const getTagName = (variant: TextVariant) => {
  switch (variant) {
    case 'titleXS':
      return 'h6';
    case 'titleS':
      return 'h5';
    case 'titleM':
      return 'h4';
    case 'titleL':
      return 'h3';
    case 'titleXL':
      return 'h2';
    case 'title2XL':
    case 'title3XL':
    case 'title4XL':
    case 'title5XL':
      return 'h1';
    default:
      return 'p';
  }
};
export const Text = ({
  as,
  tag,
  colorPath,
  variant = 'bodyM',
  className,
  dataId,
  align = 'inherit',
  children,
  id,
  style,
  translate
}: TextProps) => {
  const colorValue = colorPath && getCssColorByPath(colorPath);
  const tagName = tag || as || getTagName(variant);
  return <TextElement className={cx(className, cx(Boolean(align) && `${getLinariaClassName(TextElement)}--align`), `${getLinariaClassName(TextElement)}--typography-${variant}`)} as={tagName} colorValue={colorValue} data-id={dataId} align={align} id={id} style={style} translate={translate}>
      {children}
    </TextElement>;
};

require("./Text.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Text.tsx");